import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import { db } from '../../firebase';
import { useAuth } from '../../components/context/AuthContext';
import { getProductShareSlug } from '../../constants/site';
import { createLogger } from '../../utils/logger';
import ProductImage from '../ui/ProductImage';

const logger = createLogger('PublicProductsDashboard');

const DashboardProductImage = ({ imagePath, altText }) => (
  <ProductImage
    imagePath={imagePath}
    altText={altText}
    className="rounded"
    style={{ height: '60px', width: '60px', objectFit: 'cover', flexShrink: 0 }}
    placeholderStyle={{
      height: '60px',
      width: '60px',
      flexShrink: 0,
    }}
    loadingLabel=""
  />
);

const PublicProductsDashboard = () => {
  const { currentUser } = useAuth();
  const [bestSelling, setBestSelling] = useState([]);
  const [trending, setTrending] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      try {
        // 1. Fetch Inventory for productSupermarkets, productsWithDeals, and fallback supermarket lookup
        const inventorySnapshot = await getDocs(
          query(collection(db, 'Inventory'), where('quantity', '>', 0), limit(500))
        );

        const productSupermarkets = {}; // product_id -> first supermarket_id for link
        const productsWithDeals = new Set();

        inventorySnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const productId = data.product_id;
          const supermarketId = data.supermarket_id;

          if (productId) {
            if (!productSupermarkets[productId]) {
              productSupermarkets[productId] = supermarketId;
            }
            if (data.discount_deals && Array.isArray(data.discount_deals) && data.discount_deals.length > 0) {
              productsWithDeals.add(productId);
            }
          }
        });

        // 2. Best selling: aggregate quantity SOLD from Transactions (authenticated only - Transactions require auth)
        const soldByProduct = {};
        if (currentUser) {
          try {
            const transactionsSnapshot = await getDocs(collection(db, 'Transactions'));
            const completedStatuses = ['completed', 'packed_awaiting_pickup', 'awaiting_packing', 'payment_successful'];

            transactionsSnapshot.docs.forEach((docSnap) => {
              const data = docSnap.data();
              if (!completedStatuses.includes(data.status)) return;

              const items = data.cartItems || data.products || [];
              if (!Array.isArray(items)) return;

              const supermarketId = data.supermarketId || data.supermarket_id;
              items.forEach((item) => {
                const productId = item.productId || item.product_id;
                const qty = typeof item.quantity === 'number' ? item.quantity : 0;
                if (productId && qty > 0) {
                  soldByProduct[productId] = (soldByProduct[productId] || 0) + qty;
                  if (!productSupermarkets[productId] && supermarketId) {
                    productSupermarkets[productId] = supermarketId;
                  }
                }
              });
            });
          } catch (transErr) {
            logger.warn('[PublicProductsDashboard] Could not fetch transactions:', transErr);
          }
        }

        // 3. Best selling: top 5 by quantity sold (fallback to inventory if no sales or unauthenticated)
        let bestSellingIds = Object.entries(soldByProduct)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([id]) => id);

        if (bestSellingIds.length === 0) {
          const quantityByProduct = {};
          inventorySnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            const productId = data.product_id;
            const qty = typeof data.quantity === 'number' ? data.quantity : 0;
            if (productId) quantityByProduct[productId] = (quantityByProduct[productId] || 0) + qty;
          });
          bestSellingIds = Object.entries(quantityByProduct)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([id]) => id);
        }

        // 4. Trending: products with deals (up to 5), or newest products as fallback
        let trendingIds = Array.from(productsWithDeals).slice(0, 5);
        if (trendingIds.length < 5) {
          const recentProductsSnapshot = await getDocs(
            query(
              collection(db, 'Products'),
              orderBy('created_at', 'desc'),
              limit(10)
            )
          );
          const recentIds = recentProductsSnapshot.docs.map((d) => d.id);
          trendingIds = [...trendingIds];
          recentIds.forEach((id) => {
            if (!trendingIds.includes(id) && trendingIds.length < 5) {
              trendingIds.push(id);
            }
          });
        }

        // 5. For products without supermarketId, fetch first inventory to get supermarket
        const idsNeedingSupermarket = [...bestSellingIds, ...trendingIds].filter(
          (id) => !productSupermarkets[id]
        );
        await Promise.all(
          idsNeedingSupermarket.map(async (productId) => {
            const invQuery = query(
              collection(db, 'Inventory'),
              where('product_id', '==', productId),
              where('quantity', '>', 0),
              limit(1)
            );
            const invSnap = await getDocs(invQuery);
            if (!invSnap.empty) {
              productSupermarkets[productId] = invSnap.docs[0].data().supermarket_id;
            }
          })
        );

        // 6. Fetch product details for best selling and trending
        const productIdsToFetch = [...new Set([...bestSellingIds, ...trendingIds])];
        const productPromises = productIdsToFetch.map((id) =>
          getDoc(doc(db, 'Products', id)).then((snap) =>
            snap.exists() ? { id: snap.id, ...snap.data() } : null
          )
        );
        const productResults = await Promise.all(productPromises);
        const productsMap = {};
        productResults.forEach((p) => {
          if (p) productsMap[p.id] = p;
        });

        setBestSelling(
          bestSellingIds
            .map((id) => ({
              ...productsMap[id],
              id,
              supermarketId: productSupermarkets[id],
              totalSold: soldByProduct[id] ?? 0
            }))
            .filter((p) => p.display_name)
        );

        setTrending(
          trendingIds
            .map((id) => ({
              ...productsMap[id],
              id,
              supermarketId: productSupermarkets[id]
            }))
            .filter((p) => p && p.display_name)
        );

        // 7. Top categories from products
        const categoriesSnapshot = await getDocs(query(collection(db, 'Products'), limit(300)));
        const categoryCounts = {};
        categoriesSnapshot.docs.forEach((docSnap) => {
          const cat = docSnap.data().category || 'Uncategorized';
          categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
        });
        setTopCategories(
          Object.entries(categoryCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
        );
      } catch (err) {
        logger.error('[PublicProductsDashboard] Error fetching dashboard data:', err);
        setError('Failed to load dashboard. Products will still display.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser]);

  if (loading) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading dashboard...</span>
          </Spinner>
          <p className="text-muted mb-0 mt-2 small">Loading best sellers and trending...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning mb-4" role="alert">
        {error}
      </div>
    );
  }

  const hasData = bestSelling.length > 0 || trending.length > 0 || topCategories.length > 0;
  if (!hasData) return null;

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-primary py-2" style={{ color: '#fff' }}>
        <h5 className="mb-0" style={{ color: '#fff' }}>
          <i className="fas fa-chart-line me-2" aria-hidden="true" style={{ color: '#fff' }} />
          Dashboard Summary
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {/* Best Selling Products */}
          <Col xs={12} md={6} lg={4}>
            <Card className="h-100 border-success">
              <Card.Header className="bg-success py-2" style={{ color: '#fff' }}>
                <h6 className="mb-0" style={{ color: '#fff' }}>
                  <i className="fas fa-fire me-2" aria-hidden="true" style={{ color: '#fff' }} />
                  Best Selling
                </h6>
              </Card.Header>
              <Card.Body className="p-2">
                {bestSelling.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {bestSelling.map((product, idx) => (
                      <Link
                        key={product.id}
                        to={
                          product.supermarketId
                            ? `/supermarkets/${product.supermarketId}/products/${getProductShareSlug(product)}`
                            : `/products`
                        }
                        className="d-flex align-items-center gap-2 text-decoration-none text-dark"
                        style={{ fontSize: '0.9rem' }}
                      >
                        <span className="badge bg-success me-1" style={{ color: '#fff' }}>{idx + 1}</span>
                        <DashboardProductImage
                          imagePath={product.media?.[0]?.url}
                          altText={product.display_name}
                        />
                        <div className="flex-grow-1 text-truncate">
                          <span>{product.display_name}</span>
                          {product.totalSold != null && product.totalSold > 0 && (
                            <small className="text-muted d-block">{product.totalSold} sold</small>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0 small">No data yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Trending Products */}
          <Col xs={12} md={6} lg={4}>
            <Card className="h-100 border-warning">
              <Card.Header className="bg-warning py-2" style={{ color: '#1a1a1a' }}>
                <h6 className="mb-0" style={{ color: '#1a1a1a' }}>
                  <i className="fas fa-arrow-up me-2" aria-hidden="true" style={{ color: '#1a1a1a' }} />
                  Trending
                </h6>
              </Card.Header>
              <Card.Body className="p-2">
                {trending.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {trending.map((product, idx) => (
                      <Link
                        key={product.id}
                        to={
                          product.supermarketId
                            ? `/supermarkets/${product.supermarketId}/products/${getProductShareSlug(product)}`
                            : `/products`
                        }
                        className="d-flex align-items-center gap-2 text-decoration-none text-dark"
                        style={{ fontSize: '0.9rem' }}
                      >
                        <span className="badge bg-warning me-1" style={{ color: '#1a1a1a' }}>{idx + 1}</span>
                        <DashboardProductImage
                          imagePath={product.media?.[0]?.url}
                          altText={product.display_name}
                        />
                        <div className="flex-grow-1 text-truncate">
                          <span>{product.display_name}</span>
                          <small className="text-muted d-block">{product.category || 'Uncategorized'}</small>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0 small">No data yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Top Categories */}
          <Col xs={12} md={12} lg={4}>
            <Card className="h-100 border-primary">
              <Card.Header className="bg-primary py-2" style={{ color: '#fff' }}>
                <h6 className="mb-0" style={{ color: '#fff' }}>
                  <i className="fas fa-tags me-2" aria-hidden="true" style={{ color: '#fff' }} />
                  Top Categories
                </h6>
              </Card.Header>
              <Card.Body className="p-2">
                {topCategories.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {topCategories.map(([category, count]) => (
                      <Link
                        key={category}
                        to="/products"
                        state={{ categoryFilter: category }}
                        className="badge bg-primary text-decoration-none"
                        style={{ fontSize: '0.85rem', padding: '0.4em 0.6em', color: '#fff' }}
                      >
                        {category} ({count})
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0 small">No categories yet</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default PublicProductsDashboard;
